import { useState } from 'react';
import { WorkspaceDialog } from '../../OptimizationWorkspace/WorkspacePrimitives';
import { V2_METRICS } from './v2Metrics';
import { cpaDeterioration, fmt, isUnavailable, money, topMovers, topSpenders } from './portfolioData';
import './portfolioOverviewDrilldown.css';

export const OVERVIEW_LISTS = {
  spend: { title: 'Top spenders', metric: 'spend' },
  zero: { title: 'Spending, not converting', metric: 'spend' },
  cpa: { title: 'CPA deterioration', metric: 'cpa' },
  up: { title: 'Top movers — up', metric: 'conversions' },
  down: { title: 'Top movers — down', metric: 'conversions' },
  capped: { title: 'Capped and converting', metric: 'spend' },
};

// Remove only the preview limit. Eligibility, rank and comparison periods stay
// owned by the same helpers used by the six boxes.
export function overviewMatches(key, { rows, deltas, cpaWorse, moversUp, moversDown, capped }) {
  if (key === 'spend') return topSpenders(rows, Infinity);
  if (key === 'zero') return rows.filter(r => r.cost !== null && r.cost > 0 && r.conversions === 0).sort((a, b) => b.cost - a.cost);
  if (key === 'cpa') return isUnavailable(cpaWorse) ? cpaWorse : cpaDeterioration(deltas, rows, Infinity);
  if (key === 'up' || key === 'down') {
    const preview = key === 'up' ? moversUp : moversDown;
    return isUnavailable(preview) ? preview : topMovers(deltas, rows, { direction: key, limit: Infinity });
  }
  return (capped?.rows || []).map(row => ({ ...row, id: row.id, name: row.campaignName }));
}

function value(number, metric, currency) {
  if (number == null || !Number.isFinite(Number(number))) return 'Unavailable';
  return metric.format.startsWith('currency')
    ? (currency ? money(number, currency, metric.format === 'currency2' ? 2 : 0) : 'Unavailable')
    : fmt(number, metric.format);
}

export default function PortfolioOverviewDrilldown({ listKey, items, campaigns, rows, rangeLabel, onCampaign, onClose }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('rank');
  const definition = OVERVIEW_LISTS[listKey];
  const unavailable = isUnavailable(items);
  const byId = new Map(campaigns.map(c => [c.id, c]));
  const rowById = new Map(rows.map(row => [row.id, row]));
  const matching = unavailable ? [] : items.filter(item => (item.name || '').toLowerCase().includes(search.toLowerCase()));
  if (sort === 'name') matching.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return <WorkspaceDialog wide expandable title={definition.title} subtitle={`Selected reporting · ${rangeLabel}`} onClose={onClose}>
    <section className="ow-overview-drilldown" aria-label={`${definition.title} campaign list`}>
      {unavailable ? <p role="status">{items.needs || items.reason}</p> : <>
        <div className="ow-overview-list-controls"><label>Search campaigns<input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Campaign name" /></label>
          <label>Sort<select value={sort} onChange={event => setSort(event.target.value)}><option value="rank">Box ranking</option><option value="name">Campaign A–Z</option></select></label>
          <span role="status">{matching.length} of {items.length} campaigns</span></div>
        <p className="ow-footnote">Metrics use the selected date range. Box comparisons, when shown, use each campaign’s labeled periods. Unavailable is not zero. Monetary values retain each campaign’s currency.</p>
        <div className="ow-table-scroll"><table className="ow-table"><thead><tr><th>Campaign</th><th>Details</th><th>Currency</th>{['cpa', 'up', 'down'].includes(listKey) && <th>Box comparison</th>}{V2_METRICS.map(metric => <th key={metric.key}>{metric.label}</th>)}</tr></thead>
          <tbody>{matching.map(item => {
            const campaign = byId.get(item.id);
            const row = rowById.get(item.id);
            const metrics = campaign?.performanceV2?.metrics || row || {};
            const currency = campaign?.performanceV2?.currency || row?.currency || item.currency;
            return <tr key={item.id}><td><button className="ow-text-button" onClick={() => onCampaign(item.id, definition.metric)}>{item.name}</button><small>{row?.type || item.platform}</small></td>
              <td><button className="ow-text-button" aria-label={`More detail for ${item.name}`} onClick={() => onCampaign(item.id, definition.metric)}>More detail</button></td><td>{currency || 'Unavailable'}</td>
              {['cpa', 'up', 'down'].includes(listKey) && <td>{listKey === 'cpa' ? `${money(item.was, currency, 2)} → ${money(item.now, currency, 2)}` : `${fmt(item.previous, 'ratio')} → ${fmt(item.current, 'ratio')} conversions`}
                {item.period && <small>{item.period.daysPerPeriod} days per period · {item.period.priorRange.start}–{item.period.priorRange.end} → {item.period.currentRange.start}–{item.period.currentRange.end}</small>}</td>}
              {V2_METRICS.map(metric => <td key={metric.key}>{value(metrics[metric.key], metric, currency)}</td>)}</tr>;
          })}</tbody></table></div>
        {!matching.length && <p>{search ? 'No campaigns match your search.' : 'No matching campaigns in the available reporting.'}</p>}
      </>}
    </section>
  </WorkspaceDialog>;
}
